import React, { useEffect, useMemo, useState } from 'react';

import constants from 'constants/constants';
import { groupBy, sortBy } from 'lodash';
import moment from 'moment-timezone';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useProgramLibraryStore } from 'store/programLibrary.store';
import { useProjectTreeStore } from 'store/projectContract.store';

import { useMutationExportFromOpcXerFile } from 'api/mutations/exportFromOpcXerFile.mutation';
import { useQueryGetLanguageList } from 'api/queries/getLanguageList.query';
import { useQueryGetOpcProjectsList } from 'api/queries/getOpcProjectsList.query';
import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';

import { TreeCrumbs } from 'components/common/TreeCrumbs/TreeCrumbs';
import { selectLastSubProgram } from 'components/common/UploadFileModal/components/UploadMppModal/UploadMppModal.utils';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import classes from 'components/pages/ProgramLibraryPage/components/UploadOpcModal/UploadOpcModal.module.scss';

export interface FormProps {
    projectOpcId: number | null;
    workspaceOpcId: number | null;
    contract: any;
    project: any;
    isBaseline: boolean;
    language: { label: string; value: string } | null;
}

export const UploadOpcModal = () => {
    const { email } = useUserHook();
    const { project: storedProject, contract: storedContract } = useProjectTreeStore();
    const { showOpcModal, update } = useProgramLibraryStore();
    const [isUploading, setIsUploading] = React.useState(false);
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const { data: projectHierarchyList, isLoading: loadingGeneralList } =
        useQueryGetProgramGeneralHierarchyList('ProgramLibraryUploadFiles');
    const { mutate } = useMutationExportFromOpcXerFile();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    const onSelectionFinish = ({ project, contract }) => {
        setSelectedProject(project);
        setSelectedContract(contract);
    };

    const onClose = () => {
        update({ showOpcModal: false });
    };

    const { data: languageList } = useQueryGetLanguageList();

    const mappedLanguageList = useMemo<ISelectOption<string>[]>(() => {
        return languageList ? sortBy(languageList, 'label') : [];
    }, [languageList]);

    const selectedNode = useMemo(() => {
        return storedContract || selectLastSubProgram({ projectHierarchyList, storedProject });
    }, [storedProject, storedContract, projectHierarchyList]);

    useEffect(() => {
        selectedNode && setSelectedContract(selectedNode);
    }, [selectedNode]);

    useEffect(() => {
        storedProject && setSelectedProject(storedProject);
        storedContract && setSelectedContract(storedContract);
    }, [storedProject, storedContract]);

    const { data: opcProjectsList } = useQueryGetOpcProjectsList();

    const { control, handleSubmit, watch, setValue, reset } = useForm<FormProps>({
        defaultValues: {
            projectOpcId: null,
            workspaceOpcId: null,
            contract: null,
            project: null,
            isBaseline: false,
            language: null,
        },
    });

    useEffect(() => {
        if (selectedProject && selectedContract) {
            reset({
                projectOpcId: null,
                workspaceOpcId: null,
                contract: selectedContract,
                project: selectedProject,
                isBaseline: false,
                language: null,
            });
        }
    }, [reset, selectedContract, selectedProject]);

    const opcWorkspaces = useMemo(() => {
        if (opcProjectsList) {
            const groups = groupBy(opcProjectsList, 'workspaceOpcId');
            const workspaces = Object.keys(groups).map((key) => ({
                value: Number(key),
                label: key,
            }));
            return sortBy(workspaces, 'label');
        }
        return [];
    }, [opcProjectsList]);

    const workspaceOpcId = watch('workspaceOpcId');

    useEffect(() => {
        if (opcWorkspaces.length > 0 && workspaceOpcId === null) {
            setValue('workspaceOpcId', opcWorkspaces[0].value, { shouldDirty: true });
        }
    }, [opcWorkspaces, setValue]);

    const opcProjects = useMemo(() => {
        if (opcProjectsList && workspaceOpcId) {
            return opcProjectsList
                .filter((item) => item.workspaceOpcId === workspaceOpcId)
                .map((item) => ({
                    value: item.projectOpcId,
                    label: item.projectOpcName,
                }));
        }
        return [];
    }, [opcProjectsList, workspaceOpcId]);

    useEffect(() => {
        if (workspaceOpcId && opcProjects.length > 0) {
            setValue('projectOpcId', opcProjects[0].value, { shouldDirty: true });
        }
    }, [opcProjects, setValue, workspaceOpcId]);

    const handleFinishUpload = (data: FormProps) => {
        const projectOpcName = opcProjects.find((item) => item.value === data.projectOpcId)?.label || '';
        const versionDate = moment().format(constants.formats.date.versionDate);
        const payload = {
            projectOpcId: data.projectOpcId,
            projectOpcName: projectOpcName,
            workspaceOpcId: data.workspaceOpcId,
            uploadingUser: email,
            projectId: data.project.id,
            contractId: data.contract.id,
            isBaseline: data.isBaseline,
            contractName: data.contract.title,
            versionId: data.projectOpcId,
            versionName: projectOpcName,
            versionDate: versionDate,
            download: true,
            language: data.language?.value ?? null,
        };
        setIsUploading(true);
        mutate(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries('getProjectVersions').then(() => {
                    handleSnackBar('OPC XER file imported successfully', 'success');
                    setIsUploading(false);
                    onClose();
                });
            },
            onError: () => {
                handleSnackBar('Failed to import OPC XER file', 'error');
                setIsUploading(false);
            },
        });
    };

    return (
        <Modal
            open={showOpcModal}
            onClose={onClose}
            showCloseButton={!isUploading}
            actions={
                <>
                    <Button size={'medium'} variant={'secondary'} disabled={isUploading} onClick={onClose}>
                        Cancel
                    </Button>

                    <Button
                        type={'submit'}
                        size={'medium'}
                        variant={'primary'}
                        disabled={isUploading}
                        isLoading={isUploading}
                        onClick={handleSubmit(handleFinishUpload)}
                    >
                        Import
                    </Button>
                </>
            }
            title={'OPC Import'}
        >
            <form>
                <div className={classes.container}>
                    <div className={classes.uploadFilesHeader}>
                        <div>
                            {loadingGeneralList ? (
                                <div>Loading</div>
                            ) : (
                                selectedContract?.uuid && (
                                    <TreeCrumbs
                                        selectedNode={selectedContract.uuid}
                                        onSelectionFinish={onSelectionFinish}
                                        data={projectHierarchyList}
                                        showAddNewButton={false}
                                        showAll={false}
                                    />
                                )
                            )}
                        </div>
                        <div>
                            <Controller
                                render={({ field }) => {
                                    return (
                                        <Select
                                            {...field}
                                            options={opcWorkspaces}
                                            value={opcWorkspaces.find((item) => item.value === field.value) || null}
                                            onChange={(option) =>
                                                field.onChange(option ? (option.value as number) : null)
                                            }
                                            placeholder={'Select OPC workspace'}
                                            label={'OPC workspaces'}
                                        />
                                    );
                                }}
                                name={'workspaceOpcId'}
                                control={control}
                            />
                        </div>
                        <div>
                            <Controller
                                render={({ field }) => {
                                    const value = opcProjects.find((item) => item.value === field.value) || null;
                                    return (
                                        <Select
                                            {...field}
                                            options={opcProjects}
                                            value={value}
                                            onChange={(option) => {
                                                field.onChange(option ? (option.value as number) : null);
                                            }}
                                            placeholder={'Select OPC project'}
                                            label={'OPC projects'}
                                        />
                                    );
                                }}
                                name={'projectOpcId'}
                                control={control}
                            />
                        </div>
                    </div>

                    <div className={classes.content}>
                        <div>
                            <Controller
                                render={({ field }) => (
                                    <Switch
                                        {...field}
                                        checked={field.value}
                                        onChange={(e) =>
                                            setValue('isBaseline', e.target.checked, { shouldDirty: true })
                                        }
                                        label={'Is Baseline / Re-baseline'}
                                        labelPlacement={'start'}
                                    />
                                )}
                                name={'isBaseline'}
                                control={control}
                            ></Controller>
                        </div>

                        <div>
                            <Controller
                                render={({ field }) => (
                                    <Select<ISelectOption<string | null>>
                                        {...field}
                                        options={mappedLanguageList}
                                        isClearable={true}
                                        placeholder={'No Change'}
                                        label={'Language'}
                                    />
                                )}
                                name={`language`}
                                control={control}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    );
};
